import { useState, useEffect } from "react";
import { BaseLoading, InputGroup, PrimaryButton } from "../../../library";
import i18n from "../../../language/i18n";
import { audioDeviceSelectorStyles } from "./AudioDeviceSelectorStyles";

interface AudioDeviceSelectorProps {
  onSelectMicrophone: (id: string) => void;
  onSelectSpeaker: (id: string) => void;
  selectedMicrophoneId?: string;
  selectedSpeakerId?: string;
}

const AudioDeviceSelector = ({
  onSelectMicrophone,
  onSelectSpeaker,
  selectedMicrophoneId,
  selectedSpeakerId,
}: AudioDeviceSelectorProps) => {
  const [loading, setLoading] = useState(true);
  const [devicePermissionError, setDevicePermissionError] = useState(false);
  const [microphoneOptionList, setMicrophoneOptionList] = useState<any[]>([]);
  const [speakerOptionList, setSpeakerOptionList] = useState<any[]>([]);

  const getAudioDevices = async () => {
    setLoading(true);
    setDevicePermissionError(false);
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();

      const mics = devices.filter((device) => device.kind === "audioinput");
      const speakers = devices.filter(
        (device) => device.kind === "audiooutput",
      );

      const micOptions = mics.map((device) => ({
        value: device.deviceId,
        text: device.label || i18n.t("unnamed_device"),
      }));
      setMicrophoneOptionList(micOptions);

      const speakerOptions = speakers.map((device) => ({
        value: device.deviceId,
        text: device.label || i18n.t("unnamed_device"),
      }));
      setSpeakerOptionList(speakerOptions);

      if (!selectedMicrophoneId && mics.length > 0) {
        const defaultMic =
          mics.find((d) => d.deviceId === "default") || mics[0];
        onSelectMicrophone(defaultMic.deviceId);
      }

      if (!selectedSpeakerId && speakers.length > 0) {
        const defaultSpeaker =
          speakers.find((d) => d.deviceId === "default") || speakers[0];
        onSelectSpeaker(defaultSpeaker.deviceId);
      }
    } catch (error) {
      console.error("Error accessing media devices:", error);
      setDevicePermissionError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAudioDevices();
    navigator.mediaDevices.addEventListener("devicechange", getAudioDevices);

    return () => {
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        getAudioDevices,
      );
    };
  }, []);

  const handleOnChange = (field: string, value: string) => {
    if (field === "microphone") {
      onSelectMicrophone(value);
    } else if (field === "speaker") {
      onSelectSpeaker(value);
    }
  };

  return (
    <div className="qbi-root">
      <style>{audioDeviceSelectorStyles}</style>
      <h3 className="qbi-title">{i18n.t("audio_device_settings")}</h3>

      {loading ? (
        <div className="qbi-loading-wrapper">
          <BaseLoading
            size="small"
            primaryColor="#57C2D3"
            secondaryColor="#57C2D3"
          />
        </div>
      ) : devicePermissionError ? (
        <div className="qbi-error">
          <p>{i18n.t("microphone_permission_error")}</p>
          <div className="qbi-retry-slot">
            <PrimaryButton
              {...({
                title: i18n.t("try_again"),
                size: "small",
                textOnly: true,
                onClick: getAudioDevices,
              } as any)}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="qbi-field-mic">
            <InputGroup
              type="select"
              title={i18n.t("microphone")}
              value={selectedMicrophoneId}
              optionList={microphoneOptionList}
              onChange={(event: any) => {
                handleOnChange("microphone", event.target.value);
              }}
            />
          </div>

          <div>
            <InputGroup
              type="select"
              title={i18n.t("speaker")}
              value={selectedSpeakerId}
              optionList={speakerOptionList}
              onChange={(event: any) => {
                handleOnChange("speaker", event.target.value);
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default AudioDeviceSelector;
